import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useContent from 'store/content'
import { matchColorsBanner } from 'utils/colors'
import '../i18n'
import {
  SCPWABannerContainer,
  SCPWABannerMain,
  SCPWABannerHeader,
  SCPWABannerTitle,
  SCPWABannerBody,
  SCPWABannerContent,
  SCPWABannerTabRow,
  SCPWABannerTab,
  SCPWABannerChevron,
  SCPWABannerText,
  SCPWAInstallButton,
  SCPWAInstructions,
  SCPWAInstructionsTitle,
  SCPWAInstructionStep,
  SCPWABannerLink,
} from './styles'
import CacheBanner from './CacheBanner/CacheBanner'

function testIfToken(start_url) {
  return Number.isInteger(start_url)
}

export default function PWABanner() {
  const { t } = useTranslation()
  const content = useContent()
  const { appInfos, themeInfos } = content.get.state.app

  const [isOpen, setIsOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [installPrompt, setInstallPrompt] = useState(null)
  const [isIOSSafari, setIsIOSSafari] = useState(false)
  const [isIOSChrome, setIsIOSChrome] = useState(false)
  const [isAndroid, setIsAndroid] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)
  const [isPWAInstalled, setIsPWAInstalled] = useState(false)

  const pwaBanner = appInfos?.app_settings?.pwa_banner

  useEffect(() => {
    setIsStandalone(
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    )

    const ua = navigator.userAgent
    const ios = /iphone|ipad|ipod/i.test(ua)
    const safari = /safari/i.test(ua) && !/crios|fxios|opios/i.test(ua)
    const chrome = /crios/i.test(ua)
    setIsIOSSafari(ios && safari)
    setIsIOSChrome(ios && chrome)
    setIsAndroid(/android/i.test(ua))

    if (localStorage.getItem('pwa-installed') === 'true') {
      setIsPWAInstalled(true)
    }

    if ('getInstalledRelatedApps' in navigator) {
      navigator.getInstalledRelatedApps().then((apps) => {
        if (apps.length > 0) {
          setIsPWAInstalled(true)
          localStorage.setItem('pwa-installed', 'true')
        }
      })
    }

    const installedHandler = () => {
      setIsPWAInstalled(true)
      localStorage.setItem('pwa-installed', 'true')
    }
    window.addEventListener('appinstalled', installedHandler)

    const handler = (e) => {
      e.preventDefault()
      setInstallPrompt(e)
      setIsPWAInstalled(false)
      localStorage.removeItem('pwa-installed')
    }
    window.addEventListener('beforeinstallprompt', handler)

    if (window.__deferredInstallPrompt) {
      setInstallPrompt(window.__deferredInstallPrompt)
      window.__deferredInstallPrompt = null
    }

    setMounted(true)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', installedHandler)
    }
  }, [])

  const getStartPage = () => {
    const startPageSetting = appInfos.app_settings?.pwa_settings?.start_page
    if (startPageSetting) {
      return testIfToken(startPageSetting)
        ? `/${appInfos.id}/${startPageSetting}`
        : `/${appInfos.id}/card/${startPageSetting}`
    }
    return `/${appInfos.id}/1`
  }

  const handleInstall = async () => {
    if (!installPrompt) return
    installPrompt.prompt()
    const { outcome } = await installPrompt.userChoice
    setInstallPrompt(null)
    if (outcome === 'accepted') {
      const isNowStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true
      if (isNowStandalone) {
        location.replace(`${window.location.origin}${getStartPage()}`)
      }
    }
  }

  const bgColor =
    themeInfos?.details?.colors?.banner || '#ffffff'
  const textColor = pwaBanner?.text_color || matchColorsBanner(bgColor)
  const title = pwaBanner?.title || t('pwaBanner.title');
  const text = pwaBanner?.text || t('pwaBanner.text');
  const buttonText = pwaBanner?.button || t('pwaBanner.button');
  const instructionText = t('pwaBanner.instructionText');
  const linkText = t('pwaBanner.linkText')

  if (!mounted || isStandalone) return null

  return (
    <SCPWABannerContainer $bgColor={bgColor} $textColor={textColor}>
      <SCPWABannerMain $bgColor={bgColor} $isOpen={isOpen} $themeInfos={themeInfos}>
        <SCPWABannerHeader $isOpen={isOpen}>
          <SCPWABannerTitle $themeInfos={themeInfos} $isOpen={isOpen}>
            {title}
          </SCPWABannerTitle>
        </SCPWABannerHeader>
        <SCPWABannerBody id="pwa-banner-body" $isOpen={isOpen} aria-hidden={!isOpen}>
          <SCPWABannerContent $themeInfos={themeInfos}>
            {isPWAInstalled ? (
              isIOSSafari ? (
                <SCPWAInstructions>
                  <SCPWABannerText>{t('pwaBanner.installedText')}</SCPWABannerText>
                  <SCPWAInstructionsTitle>{t('pwaBanner.iosOpenStandaloneTitle')}</SCPWAInstructionsTitle>
                  <SCPWAInstructionStep>{t('pwaBanner.iosOpenStandaloneStep1')}</SCPWAInstructionStep>
                  <SCPWAInstructionsTitle>{t('pwaBanner.iosUninstallStepsTitle')}</SCPWAInstructionsTitle>
                  <SCPWAInstructionStep>{t('pwaBanner.iosUninstallStep1')}</SCPWAInstructionStep>
                  <SCPWAInstructionStep>{t('pwaBanner.iosUninstallStep2')}</SCPWAInstructionStep>
                  <SCPWAInstructionStep>{t('pwaBanner.iosUninstallStep3')}</SCPWAInstructionStep>
                </SCPWAInstructions>
              ) : isIOSChrome ? (
                <SCPWAInstructions>
                  <SCPWABannerText>{t('pwaBanner.installedText')}</SCPWABannerText>
                  <SCPWAInstructionsTitle>{t('pwaBanner.chromeIOSOpenStandaloneTitle')}</SCPWAInstructionsTitle>
                  <SCPWAInstructionStep>{t('pwaBanner.chromeIOSOpenStandaloneStep1')}</SCPWAInstructionStep>
                  <SCPWAInstructionsTitle>{t('pwaBanner.chromeIOSUninstallStepsTitle')}</SCPWAInstructionsTitle>
                  <SCPWAInstructionStep>{t('pwaBanner.chromeIOSUninstallStep1')}</SCPWAInstructionStep>
                  <SCPWAInstructionStep>{t('pwaBanner.chromeIOSUninstallStep2')}</SCPWAInstructionStep>
                  <SCPWAInstructionStep>{t('pwaBanner.chromeIOSUninstallStep3')}</SCPWAInstructionStep>
                </SCPWAInstructions>
              ) : isAndroid ? (
                <SCPWAInstructions>
                  <SCPWABannerText>{t('pwaBanner.installedText')}</SCPWABannerText>
                  <SCPWAInstructionsTitle>{t('pwaBanner.androidOpenStandaloneTitle')}</SCPWAInstructionsTitle>
                  <SCPWAInstructionStep>{t('pwaBanner.androidOpenStandaloneStep1')}</SCPWAInstructionStep>
                  <SCPWAInstructionsTitle>{t('pwaBanner.androidUninstallStepsTitle')}</SCPWAInstructionsTitle>
                  <SCPWAInstructionStep>{t('pwaBanner.androidUninstallStep1')}</SCPWAInstructionStep>
                  <SCPWAInstructionStep>{t('pwaBanner.androidUninstallStep2')}</SCPWAInstructionStep>
                  <SCPWAInstructionStep>{t('pwaBanner.androidUninstallStep3')}</SCPWAInstructionStep>
                </SCPWAInstructions>
              ) : (
                <SCPWAInstructions>
                  <SCPWABannerText>{t('pwaBanner.installedText')}</SCPWABannerText>
                  <SCPWAInstructionsTitle>{t('pwaBanner.desktopOpenStandaloneTitle')}</SCPWAInstructionsTitle>
                  <SCPWAInstructionStep>{t('pwaBanner.desktopOpenStandaloneStep1')}</SCPWAInstructionStep>
                  <SCPWAInstructionStep>{t('pwaBanner.desktopOpenStandaloneStep2')}</SCPWAInstructionStep>
                  <SCPWAInstructionsTitle>{t('pwaBanner.desktopUninstallStepsTitle')}</SCPWAInstructionsTitle>
                  <SCPWAInstructionStep>{t('pwaBanner.desktopUninstallStep1')}</SCPWAInstructionStep>
                  <SCPWAInstructionStep>{t('pwaBanner.desktopUninstallStep2')}</SCPWAInstructionStep>
                  <SCPWAInstructionStep>{t('pwaBanner.desktopUninstallStep3')}</SCPWAInstructionStep>
                </SCPWAInstructions>
              )
            ) : installPrompt ? (
              <>
                <SCPWABannerText>{text}</SCPWABannerText>
                <SCPWAInstallButton $themeInfos={themeInfos} onClick={handleInstall}>
                  {buttonText}
                </SCPWAInstallButton>
              </>
            ) : isIOSSafari ? (
              <SCPWAInstructions>
                <SCPWABannerText>{instructionText}</SCPWABannerText>
                <SCPWAInstructionsTitle>{t('pwaBanner.iosStepsTitle')}</SCPWAInstructionsTitle>
                <SCPWAInstructionStep>{t('pwaBanner.iosStep1')}</SCPWAInstructionStep>
                <SCPWAInstructionStep>{t('pwaBanner.iosStep2')}</SCPWAInstructionStep>
                <SCPWAInstructionStep>{t('pwaBanner.iosStep3')}</SCPWAInstructionStep>
              </SCPWAInstructions>
            ) : isIOSChrome ? (
              <SCPWAInstructions>
                <SCPWABannerText>{instructionText}</SCPWABannerText>
                <SCPWAInstructionsTitle>{t('pwaBanner.chromeIOSStepsTitle')}</SCPWAInstructionsTitle>
                <SCPWAInstructionStep>{t('pwaBanner.chromeIOSStep1')}</SCPWAInstructionStep>
                <SCPWAInstructionStep>{t('pwaBanner.chromeIOSStep2')}</SCPWAInstructionStep>
                <SCPWAInstructionStep>{t('pwaBanner.chromeIOSStep3')}</SCPWAInstructionStep>
              </SCPWAInstructions>
            ) : (
              <SCPWAInstructions>
                <SCPWABannerText>{instructionText}</SCPWABannerText>
                <SCPWAInstructionsTitle>{t('pwaBanner.genericStepsTitle')}</SCPWAInstructionsTitle>
                <SCPWAInstructionStep>{t('pwaBanner.genericInstructions')}</SCPWAInstructionStep>
              </SCPWAInstructions>
            )}
            <SCPWABannerLink href={`/${appInfos.id}/pwa`} $textColor={textColor}>
              {linkText}
            </SCPWABannerLink>
            <CacheBanner />
          </SCPWABannerContent>
        </SCPWABannerBody>
      </SCPWABannerMain>
      <SCPWABannerTabRow>
        <SCPWABannerTab
          $themeInfos={themeInfos}
          $bgColor={bgColor}
          $textColor={textColor}
          $isOpen={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-controls="pwa-banner-body"
        >
          <SCPWABannerChevron $isOpen={isOpen} aria-hidden="true" $themeInfos={themeInfos} />
        </SCPWABannerTab>
      </SCPWABannerTabRow>
    </SCPWABannerContainer>
  )
}